import React, { useRef, useState, useEffect } from 'react';
import {
  Search,
  UploadCloud,
  Play,
  Sparkles,
  X,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { TypeDropdown } from '../../common/TypeDropdown';
import { RowCountDropdown } from '../../common/RowCountDropdown';
import { SearchTemplate } from '../../../utils/icpTemplates';

interface SearchInputCardProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  tableType: 'companies' | 'people' | 'custom';
  onTableTypeChange: (type: 'companies' | 'people' | 'custom') => void;
  customType: string;
  onCustomTypeChange: (type: string) => void;
  rowCount: number;
  onRowCountChange: (count: number) => void;
  customRowCount: string;
  onCustomRowCountChange: (value: string) => void;
  showInputWarning: boolean;
  isLoading: boolean;
  isLoadingPreview: boolean;
  onSubmit: () => void;
  onFileAttach: (file: File) => void;
  onFileRemove: () => void;
  attachedFile: File | null;
  attachedFileInfo: { rowCount: number; columnCount: number } | null;
  searchTemplates: SearchTemplate[];
  placeholderIndex: number;
  isPlaceholderVisible: boolean;
  rowCountOptions?: number[];
  onReady?: (focus: () => void) => void;
  onOpenWizard?: () => void;
}

const DEFAULT_ROW_COUNT_OPTIONS = [10, 25, 50, 100];

export const SearchInputCard: React.FC<SearchInputCardProps> = ({
  searchQuery,
  onSearchQueryChange,
  tableType,
  onTableTypeChange,
  customType,
  onCustomTypeChange,
  rowCount,
  onRowCountChange,
  customRowCount,
  onCustomRowCountChange,
  showInputWarning,
  isLoading,
  isLoadingPreview,
  onSubmit,
  onFileAttach,
  onFileRemove,
  attachedFile,
  attachedFileInfo,
  searchTemplates,
  placeholderIndex,
  isPlaceholderVisible,
  rowCountOptions = DEFAULT_ROW_COUNT_OPTIONS,
  onReady,
  onOpenWizard,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showRowCountDropdown, setShowRowCountDropdown] = useState(false);
  const searchInputRef = useRef<HTMLTextAreaElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  const handleCSVFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return;
    }

    onFileAttach(file);

    // Reset file input
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (onReady) {
      onReady(() => searchInputRef.current?.focus());
    }
  }, [onReady]);

  const effectiveRowCount = customRowCount ? parseInt(customRowCount) : rowCount;

  const adjustTextareaHeight = () => {
    const textarea = searchInputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(56, textarea.scrollHeight)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleRowCountSelect = (count: number) => {
    onRowCountChange(count);
    setShowRowCountDropdown(false);
  };

  const handlePlaceholderClick = () => {
    const template = searchTemplates[placeholderIndex];
    if (template) {
      onSearchQueryChange(template.query);
      onTableTypeChange(template.type);
      searchInputRef.current?.focus();
    }
  };

  return (
    <div className="relative mb-5">
      <Card
        padding="none"
        shadow="md"
        className={`transition-all duration-300 rounded-2xl ${
          isFocused ? 'ring-1 ring-neutral-900/10 shadow-xl' : 'hover:shadow-lg'
        }`}
      >
        <div className="px-4 sm:px-5 pt-4 pb-3">
          <div className="flex gap-3">
            <div
              className={`w-8 h-8 mt-1 rounded-lg transition-all duration-200 flex-shrink-0 flex items-center justify-center ${
                isFocused ? 'bg-neutral-900' : 'bg-neutral-100'
              }`}
            >
              <Search
                className={`w-4 h-4 transition-colors duration-200 ${
                  isFocused ? 'text-white' : 'text-neutral-400'
                }`}
              />
            </div>
            <div className="flex-1 min-h-[56px]" data-tour="search-input">
              {!searchQuery && !isFocused && searchTemplates.length > 0 ? (
                <button onClick={handlePlaceholderClick} className="w-full text-left cursor-text group">
                  <span
                    className="text-sm text-neutral-400 group-hover:text-neutral-500 leading-relaxed transition-all duration-300 block"
                    style={{
                      opacity: isPlaceholderVisible ? 1 : 0,
                      transform: isPlaceholderVisible ? 'translateY(0)' : 'translateY(-4px)',
                    }}
                  >
                    {searchTemplates[placeholderIndex]?.query}
                  </span>
                </button>
              ) : (
                <textarea
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => {
                    onSearchQueryChange(e.target.value);
                    adjustTextareaHeight();
                  }}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe what you're looking for..."
                  className="w-full min-h-[56px] max-h-[200px] text-sm bg-transparent outline-none text-neutral-900 placeholder:text-neutral-400 resize-none leading-relaxed overflow-y-auto"
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end px-4 sm:px-5 pb-3">
          <button
            onClick={onOpenWizard}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-neutral-400 hover:text-neutral-600 transition-colors rounded-lg"
            data-tour="ask-ai-button"
          >
            <Sparkles className="w-3 h-3" />
            <span className="text-[11px]">
              Need ideas? <span className="font-medium">Ask AI</span>
            </span>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 px-4 sm:px-5 py-3 border-t border-neutral-100">
          <div className="grid grid-cols-3 sm:flex sm:items-center gap-1.5 sm:gap-2">
            <TypeDropdown
              tableType={tableType}
              customType={customType}
              showDropdown={showTypeDropdown}
              onToggleDropdown={() => setShowTypeDropdown(!showTypeDropdown)}
              onCloseDropdown={() => setShowTypeDropdown(false)}
              onTypeChange={onTableTypeChange}
              onCustomTypeChange={onCustomTypeChange}
            />

            <RowCountDropdown
              rowCount={rowCount}
              customRowCount={customRowCount}
              effectiveRowCount={effectiveRowCount}
              showDropdown={showRowCountDropdown}
              showInputWarning={showInputWarning}
              options={rowCountOptions}
              onToggleDropdown={() => setShowRowCountDropdown(!showRowCountDropdown)}
              onCloseDropdown={() => setShowRowCountDropdown(false)}
              onSelect={handleRowCountSelect}
              onCustomChange={onCustomRowCountChange}
            />

            <Button
              variant="secondary"
              size="sm"
              onClick={() => csvInputRef.current?.click()}
              disabled={isLoadingPreview || !!attachedFile}
              leftIcon={<UploadCloud className="w-3 h-3" />}
              className="justify-center w-full sm:w-auto"
              data-tour="csv-upload"
            >
              CSV
            </Button>
            {/* Hidden file input for inline attachment */}
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              onChange={handleCSVFileSelect}
              className="hidden"
            />
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={onSubmit}
            disabled={(!searchQuery.trim() && !attachedFile) || isLoading || isLoadingPreview}
            loading={isLoadingPreview}
            leftIcon={!isLoadingPreview ? <Play className="w-3 h-3" /> : undefined}
            className="w-full sm:w-auto bg-neutral-900 hover:bg-neutral-800"
            data-tour="run-search-button"
          >
            Run
          </Button>
        </div>

        {/* Attached file indicator */}
        {attachedFile && (
          <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-t border-neutral-100 bg-neutral-50/50">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-neutral-900 truncate">
                  {attachedFile.name}
                </p>
                {attachedFileInfo && (
                  <p className="text-[11px] text-neutral-500">
                    {attachedFileInfo.rowCount.toLocaleString()} rows • {attachedFileInfo.columnCount} columns
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onFileRemove}
              className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};
